import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import { SendIcon, UndoIcon } from '@treams/treams-icons';
import { AiChat, AiChatMessage, AiChatMessageSender } from '@treams/treams-network';
import {
	Avatar,
	Button,
	Chat,
	FlexGap,
	Input,
	Modal,
	Paragraph,
	UnderlineLink,
	chatSlideIn,
	tokens,
} from '@treams/treams-ui';

const bulletBefore = css`
	flex-direction: row;

	&::before {
		display: block;
		height: 100%;
		content: '●';
		margin-right: 0.5em;
	}
`;

const ChatBubbleContainer = styled(Chat.Bubble)<{ $isBulletPoint?: boolean }>`
	${(props) => props.$isBulletPoint && bulletBefore};
`;

function ChatBubble(props: { isAI?: boolean; message: string }) {
	const isBulletPoint = props.message.startsWith('• ');

	return (
		<ChatBubbleContainer type={props.isAI ? 'ai' : 'user'} $isBulletPoint={isBulletPoint}>
			{isBulletPoint ? props.message.slice(2) : props.message}
		</ChatBubbleContainer>
	);
}

const Messages = styled(Box)`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: ${tokens.padding.container}px;
	background: ${tokens.surface.white};
`;

function BubbleGroup(props: {
	isAI?: boolean;
	messages: string[];
	onMessageDisplayed?: () => void;
	onStoppedTyping?: () => void;
}) {
	const [messagesShown, setMessagesShown] = useState(0);

	if (!props.isAI && messagesShown != props.messages.length) {
		setMessagesShown(props.messages.length);
	}

	const typingOnly = props.messages.length === 0;
	const isTyping = props.isAI && (typingOnly || messagesShown < props.messages.length);

	const viewer = useAuthenticatedUser();

	const bubbles = props.messages.slice(0, messagesShown).map((message, i) => (
		<Flex mt={i === 0 ? 0 : 1} key={i}>
			<ChatBubble isAI={props.isAI} message={message} />
		</Flex>
	));

	const showNewMessage = (): boolean => {
		if (messagesShown === props.messages.length) {
			return false;
		}

		if (messagesShown + 1 == props.messages.length) {
			props.onStoppedTyping?.();
		}

		setMessagesShown(messagesShown + 1);

		return true;
	};

	useEffect(() => {
		if (!typingOnly && messagesShown < props.messages.length) {
			const timer = setTimeout(() => showNewMessage(), 1000);
			return () => clearTimeout(timer);
		}
	}, [messagesShown, typingOnly]);

	useEffect(() => {
		props.onMessageDisplayed?.();
	}, [messagesShown]);

	return (
		<FlexGap
			my={`${tokens.gap.list}px`}
			alignItems="flex-start"
			flexDirection={props.isAI ? 'row' : 'row-reverse'}
			gap={tokens.gap.list}
		>
			<Chat.Avatar mt="3px" flexGrow={0} flexShrink={0}>
				{props.isAI ? <Avatar.AI size={32} /> : <Avatar user={viewer} size={32} />}
			</Chat.Avatar>
			<Box alignItems={props.isAI ? 'flex-start' : 'flex-end'} maxWidth="80%">
				{bubbles}
				{isTyping && <Chat.Typing mt={messagesShown === 0 ? 0 : 1} key={messagesShown} />}
			</Box>
		</FlexGap>
	);
}

export const MessageInputBox = styled(Box)`
	border: none;
	padding: ${tokens.padding.control}px;
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	font-family: ${tokens.typography.fontFamily};
	width: 100%;
	appearance: none;
	background: ${tokens.surface.white};
	border-radius: ${tokens.radius.sm}px;
	box-shadow: ${tokens.shadow.input};

	&:focus-within {
		outline: none;
		background: ${tokens.surface.white};
		box-shadow: 0 0 0 2px ${tokens.border.focus};
	}
`;

type AIConversation = { messages: string[]; isAI: boolean }[];

function groupMessages(messages: AiChatMessage[]): AIConversation {
	return messages.reduce((groupedMessages: AIConversation, message) => {
		const isAI = message.sender === AiChatMessageSender.Ai;
		const newGroup =
			groupedMessages.length === 0 || groupedMessages[groupedMessages.length - 1].isAI !== isAI;

		if (newGroup) {
			return groupedMessages.concat([
				{
					messages: [message.text],
					isAI,
				},
			]);
		} else {
			return [
				...groupedMessages.slice(0, -1),
				{
					messages: [...groupedMessages[groupedMessages.length - 1].messages, message.text],
					isAI,
				},
			];
		}
	}, []);
}

const SuggestionWrapper = styled(Box)`
	margin-left: ${32 + tokens.gap.list}px;
	animation: 0.35s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-fill-mode: backwards;
`;

const PreConversationAccessoryWrapper = styled(Box)`
	display: flex;
	justify-content: center;

	animation: 0.35s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-delay: 250ms;
	animation-fill-mode: backwards;
`;

export function SuggestionsAIChatModal<Suggestion>(props: {
	initialInput?: string;
	onDismiss: () => void;
	startChat: (reset: boolean) => Promise<{ chat: AiChat }>;
	starting: boolean;
	advanceChat: (
		chatID: string,
		userResponse: string,
	) => Promise<{ chat: AiChat; suggestion: Suggestion | null }>;
	advancing: boolean;
	renderSuggestion: (suggestion: Suggestion) => React.ReactNode;
	translationKey: string;
	isPreConversation?: boolean;
	preConversationMessages?: string[];
	preConversationAccessory?: ReactNode;
	preConversationResult?: string | null;
	onReset?: () => void;
}) {
	const { t } = useTranslation();
	const [input, setInput] = useState(props.initialInput ?? '');
	const viewer = useAuthenticatedUser();
	const [reset, setReset] = useState(false);
	const [chatID, setChatID] = useState<string | null>(null);
	const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
	const [showSuggestion, setShowSuggestion] = useState(false);

	const preConversation = props.preConversationMessages
		? [
				{
					messages: props.preConversationMessages,
					isAI: true,
				},
				...(props.preConversationResult
					? [{ messages: [props.preConversationResult], isAI: false }]
					: []),
			]
		: [];

	const [conversation, setConversation] = useState<AIConversation>(preConversation);

	const [inputAllowed, setInputAllowed] = useState(false);
	const inputEnabled = inputAllowed && input.trim().length > 0 && !props.isPreConversation;

	const endRef = useRef<null | HTMLDivElement>(null);

	const submitInput = async () => {
		if (conversation.length > 0 && conversation[conversation.length - 1].isAI === false) {
			const extendedLastGroup = {
				messages: [...conversation[conversation.length - 1].messages, input],
				isAI: false,
			};

			setConversation(conversation.slice(0, -1).concat([extendedLastGroup]));
		} else {
			setConversation([
				...conversation,
				{
					messages: [input],
					isAI: false,
				},
			]);
		}
		setInput('');
		setInputAllowed(false);

		const { chat, suggestion } = await props.advanceChat(chatID!, input);
		setConversation(preConversation.concat(groupMessages(chat.messages)));

		if (suggestion) {
			setSuggestion(suggestion);
		}
	};

	const noConversation = conversation.length === 0;

	useEffect(() => {
		(async () => {
			if (!chatID && !props.isPreConversation && !props.starting) {
				setInputAllowed(false);

				const { chat } = await props.startChat(reset);

				setChatID(chat.id);
				setConversation(preConversation.concat(groupMessages(chat.messages)));
			}

			if (noConversation && props.isPreConversation) {
				setInputAllowed(false);
				setConversation(preConversation);
			}
		})();
	}, [noConversation, chatID, props.isPreConversation]);

	// Scroll to bottom when messages change
	const scrollIntoView = () => {
		endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	useEffect(() => {
		if (suggestion) {
			scrollIntoView();
		}
	}, [suggestion, showSuggestion]);

	const sendInitialInput = useCallback(() => {
		const hasInput = props.initialInput && props.initialInput.length > 0;
		const canSendInput = props.initialInput === input && inputAllowed;

		if (hasInput && canSendInput) {
			submitInput();
		}
	}, [props.initialInput, inputAllowed]);

	useEffect(() => {
		sendInitialInput();
	}, [sendInitialInput]);

	const resetConversation = () => {
		setReset(true);
		setSuggestion(null);
		setShowSuggestion(false);
		setConversation([]);
		setChatID(null);
		props.onReset?.();
	};

	const loadingConversation = conversation.concat(
		props.advancing ? [{ messages: [], isAI: true }] : [],
	);

	return (
		<Modal width={600} height={680}>
			<Modal.Header
				title={t(`${props.translationKey}.ai.modal.title`)}
				onClose={props.onDismiss}
				rightAccessory={
					<Button
						type="secondary"
						small
						icon={<UndoIcon />}
						title={t('ai.reset')}
						flexGrow={0}
						flexShrink={0}
						onClick={() => resetConversation()}
					/>
				}
			/>
			<Modal.Contents
				p={0}
				display="flex"
				flexDirection="column"
				minHeight={0}
				overflow="hidden"
				bg={tokens.surface.white}
			>
				<Messages>
					{loadingConversation.map((group, index) => (
						<BubbleGroup
							key={index}
							messages={group.messages.map((message) =>
								group.isAI ? message.replaceAll('{username}', viewer.firstName) : message,
							)}
							isAI={group.isAI}
							onMessageDisplayed={() => scrollIntoView()}
							onStoppedTyping={() => setInputAllowed(true)}
						/>
					))}
					{suggestion && (
						<>
							<BubbleGroup
								messages={[t(`${props.translationKey}.ai.suggestion`)]}
								isAI={true}
								onStoppedTyping={() => setShowSuggestion(true)}
							/>
							{showSuggestion && (
								<SuggestionWrapper>{props.renderSuggestion(suggestion)}</SuggestionWrapper>
							)}
						</>
					)}
					{props.isPreConversation && inputAllowed && (
						<PreConversationAccessoryWrapper>
							{props.preConversationAccessory}
						</PreConversationAccessoryWrapper>
					)}
					<div ref={endRef} />
				</Messages>
			</Modal.Contents>
			<Modal.Footer>
				<FlexGap flexDirection="column" gap={tokens.gap.list} width="100%">
					<FlexGap gap={tokens.gap.list} alignItems="center">
						<Box flex={1} minWidth={0}>
							<Input
								value={input}
								onChange={setInput}
								autoFocus
								onKeyDown={(event) => {
									if (event.key === 'Enter' && inputEnabled) {
										submitInput();
										event.preventDefault();
									}
								}}
							/>
						</Box>
						<Button
							title={t('goals.ai.input.submit')}
							icon={<SendIcon />}
							type="ai"
							disabled={!inputEnabled}
							onClick={() => {
								submitInput();
								return;
							}}
							flexShrink={0}
						/>
					</FlexGap>
					<Paragraph small textAlign="center" mb={0}>
						{t('ai.disclaimer.text')}{' '}
						<UnderlineLink
							href={t('ai.disclaimer.link.to')}
							target="_blank"
							rel="noopener noreferrer"
							small
						>
							{t('ai.disclaimer.link.text')}
						</UnderlineLink>
					</Paragraph>
				</FlexGap>
			</Modal.Footer>
		</Modal>
	);
}
